<script lang="ts" setup>
import BreadCrumb from '#components/breadCrumb/BreadCrumb.vue';
import { CARREFOUR_REFERENTIAL_INTERVAL_KEY } from '#components/dynamicForm/fields/PeriodField/constants/periodField.constant';
import FilterForm from '#components/dynamicForm/FilterForm.vue';
import ButtonBase from '#components/elements/button/ButtonBase.vue';
import AppHeader from '#components/layouts/AppLayoutComponents/AppHeader.vue';
import LayoutPage from '#components/layouts/LayoutPage.vue';
import { useFilterDefinition } from '#composables/filterDefinition/useFilterDefinition';
import { useLanguage } from '#composables/useLanguage';
import { REFERENTIAL_PERIOD } from '#domain/core/filter/components/periodSelector/constants/referentialPeriod.constant.ts';
import type { ReferentialPeriodType } from '#domain/core/filter/components/periodSelector/types/referentialPeriod.type.ts';
import type { IconName } from '#root/components/icons/icon.model.ts';
import { useFormFieldsStore } from '#stores/formFields.store';
import type { TabItemType } from '@c4-links/vue3-links-components';
import { C4Drawer, C4Tab } from '@c4-links/vue3-links-components';
import {
  DEFAULT_CARREFOUR_REFERENTIAL_INTERVAL,
  type CarrefourReferentialIntervalType,
  type PerimeterFilterViewType
} from '@carrefour-gcs/shared';
import { computed, provide, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import KpiPerimeterSelection from './components/KpiPerimeterSelection.vue';

const formStore = useFormFieldsStore();
const { data, error, isFetching, isSuccess } = useFilterDefinition();

const router = useRouter();
const { t } = useLanguage();

// Immediate: each page mounts its own layout, and the definitions of a page already visited come from the
// query cache at mount time, so `data` would never change and the store would keep the previous page ones.
watch(
  () => ({ data: data.value }),
  (state) => {
    if (isSuccess.value && state.data) {
      formStore.setDefinitions({
        error: error.value,
        response: state.data,
        isSuccess: isSuccess.value,
        isFetching: isFetching.value,
        isError: error.value !== null
      });
    }
  },
  { immediate: true }
);

const fields = computed(() => formStore.fields);
const groupNames = computed(() =>
  Array.from(new Set(fields.value.map((e) => e.group?.name))).filter((e) => e !== undefined)
);

const drawerOpen = ref(false);
const drawerSelectedTab = ref('perimeter');

const drawerTabs = computed<({ displayOrder: number } & TabItemType)[]>(() => {
  const data = fields.value
    .reduce(
      (acc, val) => {
        if (!acc.some((e) => e.id === val.group?.name)) {
          acc.push({
            id: val.group?.name ?? '',
            label: t(`filters.group.${val.group?.name}`),
            icon: val.group?.icon as IconName,
            displayOrder: val.group?.displayOrder ?? 0,
            badge: String(
              Object.entries(formStore.currentFieldsValue[val.group?.name ?? ''] ?? {}).reduce((accBadge, curr) => {
                if (Array.isArray(curr[1])) {
                  accBadge += curr[1].length;
                } else if (curr[1] != undefined) {
                  accBadge += 1;
                }

                return accBadge;
              }, 0)
            )
          });
        }

        return acc;
      },
      [] as ({ displayOrder: number } & TabItemType)[]
    )
    .sort((a, b) => a.displayOrder - b.displayOrder);

  return data;
});

watch(
  drawerTabs,
  (tabs) => {
    const hasSelectedTab = tabs.some((tab) => tab.id === drawerSelectedTab.value);

    if (!hasSelectedTab) {
      drawerSelectedTab.value = tabs[0]?.id ?? 'perimeter';
    }
  },
  { immediate: true }
);

const selectedFiltersNumber = computed(() =>
  Object.values(formStore.currentFieldsValue).reduce((acc, val) => {
    Object.values(val).forEach((e) => {
      if (Array.isArray(e)) {
        return (acc += e.length);
      }
      if (e != undefined) {
        return (acc += 1);
      }
    });
    return acc;
  }, 0)
);

const {
  hasTaxButton = true,
  taxonomySelector = { onlyCarrefour: false },
  hasStorePerimeterSelector = true,
  perimeterFilterView = undefined,
  referentialPeriod = REFERENTIAL_PERIOD,
  carrefourReferentialIntervals = DEFAULT_CARREFOUR_REFERENTIAL_INTERVAL,
  hasComparisonPeriod = false
} = defineProps<{
  title: string;
  hasBackButton?: boolean;
  hasTaxButton?: boolean;
  taxonomySelector?: { onlyCarrefour: boolean };
  hasStorePerimeterSelector?: boolean;
  perimeterFilterView?: PerimeterFilterViewType;
  referentialPeriod?: ReferentialPeriodType;
  carrefourReferentialIntervals?: keyof CarrefourReferentialIntervalType;
  hasComparisonPeriod?: boolean;
}>();

// The dynamic filter drawer reads the same referential intervals as the legacy period popup
provide(
  CARREFOUR_REFERENTIAL_INTERVAL_KEY,
  computed(() => carrefourReferentialIntervals)
);
</script>

<template>
  <LayoutPage>
    <template #header>
      <AppHeader />
    </template>

    <template #header-content>
      <div class="space-y-2">
        <ButtonBase
          v-if="hasBackButton"
          :label="t('commonLabel.back')"
          variant="default"
          icon-left="arrow-left-circle"
          @click="router.push({ name: 'performance' })"
        />

        <!-- Replace this by a breadcrumb -->
        <BreadCrumb />

        <div class="flex justify-between">
          <div class="flex row gap-4 items-center">
            <slot name="title-start-icon" />
            <h1 class="text-4xl font-bold">{{ title }}</h1>
          </div>
          <slot name="linkButton" />
        </div>
      </div>
      <div>
        <div class="p-8">
          <C4Drawer
            v-model:is-open="drawerOpen"
            size="medium"
            :title="t('filters.drawer.title')"
            sr-close="close"
            class="overflow-scroll h-full"
          >
            <template #default>
              <div class="flex-1 overflow-y-auto">
                <C4Tab v-model="drawerSelectedTab" class="!p-0" :tabs="drawerTabs" />

                <template v-for="groupName in groupNames" :key="groupName">
                  <!-- Use v-show instead of v-if to keep the component alive -->
                  <div v-show="drawerSelectedTab === groupName" class="mt-4">
                    <FilterForm
                      :group-name="groupName"
                      :is-active="drawerSelectedTab === groupName"
                      :fields="fields.filter((e) => e.group?.name === groupName)"
                      @cancel="() => (drawerOpen = false)"
                      @submitted="() => (drawerOpen = false)"
                    />
                  </div>
                </template>
              </div>

              <div id="drawer-button-bar"></div>
            </template>
          </C4Drawer>
        </div>
      </div>

      <KpiPerimeterSelection
        :has-tax-button="hasTaxButton"
        :taxonomy-selector="taxonomySelector"
        :has-store-perimeter-selector="hasStorePerimeterSelector"
        :perimeter-filter-view="perimeterFilterView"
        :referential-period="referentialPeriod"
        :carrefour-referential-intervals="carrefourReferentialIntervals"
        :has-comparison-period="hasComparisonPeriod"
      >
        <template #newFiltersButton>
          <ButtonBase
            type="button"
            variant="secondary"
            icon-left="filter"
            :disabled="isFetching"
            :is-loading="isFetching"
            @click="drawerOpen = true"
          >
            <span class="font-bold">
              {{ t('filters.button.label', { nbFiltersSelected: selectedFiltersNumber }) }}
            </span>
          </ButtonBase>
        </template>
      </KpiPerimeterSelection>
    </template>

    <div class="space-y-4">
      <slot />
    </div>
  </LayoutPage>
</template>
